import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Play, Clock, Euro, Search, Filter, BookOpen, Sparkles, Mail, Trophy, Lock, Bell, ChevronLeft, ChevronRight, Car, Anchor, Bike, Sofa, ArrowRight } from 'lucide-react';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { toast } from 'sonner';
import { RotatingWords } from '../components/RotatingWords';

// Real Benki YouTube videos (from his channel "La Sellerie tout simplement")
const TEASER_VIDEOS = [
  { id: 'avk24f0OVdA', title: 'Construction d\'une pergola en cuir', label: 'Habitat' },
  { id: 'FIQUyH5cabU', title: 'Surpiqûre selle moto sport', label: 'Moto' },
  { id: 'JYEl6XnVdf0', title: 'Présentation de la Sellerie Académie', label: 'Découverte' },
  { id: '9SShTEM03_Q', title: 'Atelier en action — La fabrique', label: 'Atelier' },
];

const ElearningTeaserPublic = ({ teaserEmail, setTeaserEmail, handleTeaserSubmit }) => {
  const [activeVideo, setActiveVideo] = useState(0);
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveVideo((prev) => (prev + 1) % TEASER_VIDEOS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const prev = () => setActiveVideo((p) => (p - 1 + TEASER_VIDEOS.length) % TEASER_VIDEOS.length);
  const next = () => setActiveVideo((p) => (p + 1) % TEASER_VIDEOS.length);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#172239]">
      {/* Layer 1 — heavily blurred video background. The strong blur on the iframe itself
          creates the actual depth-of-field bokeh; the layered glass overlays handle tone. */}
      <div className="absolute inset-0 z-0">
        {TEASER_VIDEOS.map((v, i) => (
          <div
            key={v.id}
            className={`absolute inset-0 transition-opacity duration-[2000ms] ease-in-out ${
              i === activeVideo ? 'opacity-95' : 'opacity-0'
            }`}
            style={{
              filter: 'blur(22px) saturate(170%) brightness(120%) contrast(105%)',
              transform: 'scale(1.12)',
              willChange: 'filter',
            }}
          >
            <iframe
              className="absolute top-1/2 left-1/2 min-w-[177.78vh] min-h-[56.25vw] w-auto h-auto -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{ width: '177.78vh', height: '100vh' }}
              src={`https://www.youtube.com/embed/${v.id}?autoplay=1&mute=1&loop=1&playlist=${v.id}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
              title={v.title}
              frameBorder="0"
              allow="autoplay; encrypted-media"
            />
          </div>
        ))}
      </div>

      {/* Layer 2 — frosted glass pane (true macOS Big Sur / iOS depoli look).
          Light tint + strong backdrop-blur ⇒ the blurred video stays visible
          as colored bokeh through the glass without any noise. */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          backdropFilter: 'blur(14px) saturate(160%)',
          WebkitBackdropFilter: 'blur(14px) saturate(160%)',
          backgroundColor: 'rgba(23, 34, 57, 0.28)',
        }}
      />
      {/* Layer 3 — soft glass tint + top-edge highlight + bottom vignette */}
      <div className="absolute inset-0 z-[2] pointer-events-none bg-gradient-to-b from-white/[0.06] via-transparent to-[#172239]/55" />
      <div className="absolute inset-0 z-[2] pointer-events-none bg-gradient-to-tr from-[#1F2A4A]/15 via-transparent to-[#C9B896]/[0.05]" />

      {/* CSS-only "frost dust" — fine dotted pattern, NOT noise. Very subtle. */}
      <div className="absolute inset-0 z-[3] pointer-events-none mix-blend-overlay opacity-[0.035]"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.55) 1px, transparent 1px)',
          backgroundSize: '5px 5px',
        }}
      />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col items-center pt-32 pb-12">
          {/* Centered hero — "BIENTÔT DISPONIBLE" is the dominant headline, the rest is supporting copy. */}
          <div className="text-center px-6 max-w-4xl mx-auto mb-8 animate-fade-in-up">
            <div className="inline-flex items-center gap-2 mb-3">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C9B896] opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#C9B896]" />
              </span>
            </div>
            <h1
              className="font-heading font-light text-[#C9B896] leading-[0.95] tracking-tight uppercase text-[clamp(1.75rem,5vw,4.25rem)]"
              data-testid="elearning-coming-title"
            >
              Bientôt
              <br />
              <span className="font-serif italic text-white/95">disponible</span>
            </h1>

            {/* Dynamic loading bar — animated indeterminate, no fixed % */}
            <div className="mt-7 max-w-md mx-auto" data-testid="elearning-progress">
              <div className="h-1.5 rounded-full bg-white/8 overflow-hidden relative border border-[#C9B896]/15">
                <div className="elearning-bar-indeterminate absolute inset-y-0 rounded-full bg-gradient-to-r from-transparent via-[#C9B896] to-transparent" />
              </div>
            </div>

            <p className="font-serif italic text-white/70 text-[clamp(0.85rem,1.3vw,1.1rem)] leading-snug mt-5">
              <RotatingWords
                words={[
                  'à ton rythme.',
                  'où tu veux.',
                  'quand tu veux.',
                  'depuis ton atelier.',
                  'sur ta tablette.',
                ]}
                interval={3000}
              />
            </p>
            <p className="text-[11px] md:text-xs text-white/55 max-w-lg mx-auto leading-relaxed mt-3 font-light">
              Des heures de vidéo tournées en atelier avec Benki. Une première leçon gratuite par formation,
              puis débloque la suite et avance à ta convenance.
            </p>
          </div>

          <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              {/* Left: form + stats (slightly smaller now) */}
              <div className="lg:col-span-7">
                <form onSubmit={handleTeaserSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mb-7 animate-fade-in-up animation-delay-200">
                  <div className="relative flex-1">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#C9B896]" />
                    <input
                      type="email"
                      value={teaserEmail}
                      onChange={(e) => setTeaserEmail(e.target.value)}
                      placeholder="ton@email.com"
                      className="w-full pl-11 pr-4 py-3 bg-white/10 backdrop-blur border border-white/20 rounded-full text-white placeholder:text-white/50 focus:outline-none focus:border-[#C9B896] text-sm"
                      data-testid="teaser-public-email"
                    />
                  </div>
                  <Button type="submit" className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] font-medium px-7 rounded-full whitespace-nowrap btn-premium text-sm" data-testid="teaser-public-submit">
                    <Bell className="w-4 h-4 mr-2" /> M&apos;avertir
                  </Button>
                </form>

                {/* Mini stats */}
                <div className="grid grid-cols-3 gap-3 max-w-lg animate-fade-in-up animation-delay-300">
                  {[
                    { icon: Play, label: '100+ épisodes', sub: 'à venir' },
                    { icon: Trophy, label: 'Gamifié', sub: 'XP & badges' },
                    { icon: Lock, label: 'Module 1', sub: 'gratuit' },
                  ].map((b, i) => {
                    const I = b.icon;
                    return (
                      <div key={i} className="p-3 bg-white/5 backdrop-blur rounded-2xl border border-white/10">
                        <I className="w-3.5 h-3.5 text-[#C9B896] mb-1.5" />
                        <p className="text-xs font-semibold text-white">{b.label}</p>
                        <p className="text-[10px] text-white/60">{b.sub}</p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right: Video preview cards (slightly smaller) */}
              <div className="lg:col-span-5">
                <div className="relative">
                  {/* Current focused video preview */}
                  <div className="relative aspect-video rounded-3xl overflow-hidden shadow-2xl border border-white/15 bg-[#1F2A4A] mb-4">
                    {TEASER_VIDEOS.map((v, i) => (
                      <div
                        key={v.id}
                        className={`absolute inset-0 transition-all duration-700 ${
                          i === activeVideo ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
                        }`}
                      >
                        <img
                          src={`https://img.youtube.com/vi/${v.id}/maxresdefault.jpg`}
                          alt={v.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.src = `https://img.youtube.com/vi/${v.id}/hqdefault.jpg`; }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1F2A4A] via-[#1F2A4A]/30 to-transparent" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-[#C9B896]/95 flex items-center justify-center shadow-2xl backdrop-blur">
                            <Lock className="w-5 h-5 text-[#1F2A4A]" />
                          </div>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-5">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-[#C9B896] bg-[#C9B896]/15 px-2.5 py-1 rounded-full">{v.label}</span>
                          <p className="text-white font-medium mt-2 text-base">{v.title}</p>
                          <div className="flex items-center gap-2 mt-1.5 text-[11px] text-white/70">
                            <Lock className="w-3 h-3" /> Verrouillé · Bientôt disponible
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Carousel controls */}
                    <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 backdrop-blur hover:bg-[#C9B896] hover:text-[#1F2A4A] text-white flex items-center justify-center transition-all z-10" data-testid="teaser-prev">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 backdrop-blur hover:bg-[#C9B896] hover:text-[#1F2A4A] text-white flex items-center justify-center transition-all z-10" data-testid="teaser-next">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Dots indicator */}
                  <div className="flex items-center justify-center gap-2">
                    {TEASER_VIDEOS.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveVideo(i)}
                        data-testid={`teaser-dot-${i}`}
                        className={`h-1.5 rounded-full transition-all duration-300 ${
                          i === activeVideo ? 'w-10 bg-[#C9B896]' : 'w-1.5 bg-white/30 hover:bg-white/60'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Category quick-jump — "EN ATTENDANT" is the dominant headline, supporting copy reduced. */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-8 pb-20" data-testid="elearning-categories">
          <div className="text-center mb-10">
            <h2 className="font-heading text-[#C9B896] text-[clamp(2rem,5.5vw,4.5rem)] leading-[0.95] font-light uppercase tracking-tight">
              En attendant<span className="elearning-dots" aria-hidden="true"><span /><span /><span /></span>
            </h2>
            <p className="font-serif italic text-white text-[clamp(1rem,1.7vw,1.5rem)] leading-tight mt-2.5">
              Rejoins-nous{' '}
              <span className="text-[#C9B896]">
                <RotatingWords
                  words={['à l\u2019atelier.', 'en présentiel.', 'à Istres.', 'dans la chaleur du cuir.']}
                  interval={3200}
                />
              </span>
            </p>
            <p className="text-white/55 text-[11px] md:text-xs mt-3 max-w-md mx-auto font-light">
              Chaque spécialité a son parcours dédié. Clique sur ta passion pour découvrir le programme jour par jour.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-5xl mx-auto">
            {[
              { key: 'auto', label: 'Automobile', sub: 'Restauration de sièges & ciel de toit', Icon: Car },
              { key: 'moto', label: 'Moto', sub: 'Selles custom et café racer', Icon: Bike },
              { key: 'nautique', label: 'Nautique', sub: 'Cabine, taud, banquettes marine', Icon: Anchor },
              { key: 'habitat', label: 'Habitat & médical', sub: 'Mobilier intérieur, sièges médicaux', Icon: Sofa },
            ].map((c) => (
              <Link
                key={c.key}
                to={withLangPrefix(`/formations-sur-place?category=${c.key}`, lang)}
                className="group relative block bg-white/5 hover:bg-[#C9B896]/15 border border-white/10 hover:border-[#C9B896]/60 rounded-2xl p-5 transition-all backdrop-blur"
                data-testid={`elearning-cat-${c.key}`}
              >
                <c.Icon className="w-6 h-6 text-[#C9B896] mb-4 group-hover:scale-110 transition-transform" />
                <p className="text-white font-medium text-base mb-1">{c.label}</p>
                <p className="text-xs text-white/55 leading-snug min-h-[2.5em]">{c.sub}</p>
                <div className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#C9B896] opacity-0 group-hover:opacity-100 transition-opacity">
                  Voir le parcours <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom CTA — to onsite formations */}
        <div className="relative z-10 border-t border-white/10 backdrop-blur-md bg-[#172239]/60">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
            <div>
              <p className="text-sm text-white/65 mb-1">Une question, une suggestion&nbsp;?</p>
              <p className="text-lg text-white font-medium">Écris-nous, on te répond sous 24&nbsp;h.</p>
            </div>
            <a
              href="mailto:assistance.sellerie@gmail.com?subject=E-learning%20Sellerie%20Acad%C3%A9mie"
              className="inline-flex items-center gap-2 bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full px-8 py-3 font-medium btn-premium whitespace-nowrap text-sm"
              data-testid="teaser-email-cta"
            >
              <Mail className="w-4 h-4" /> Nous écrire
            </a>
            <Link to={L('/formations-sur-place')}>
              <Button size="lg" className="bg-transparent text-white border border-[#C9B896]/60 hover:bg-[#C9B896]/15 hover:border-[#C9B896] rounded-full px-8 py-6 font-medium whitespace-nowrap" data-testid="teaser-onsite-cta">
                Voir les formations sur place →
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const categoryLabels = {
  decouverte: 'Découverte',
  complete: 'Formation Complète',
  auto: 'Sellerie Auto',
  moto: 'Sellerie Moto',
  nautique: 'Sellerie Nautique',
  habitat: 'Habitat & Médical',
  dessin: 'Dessin Technique'
};

const categoryImages = {
  decouverte: 'https://images.unsplash.com/photo-1625479142928-c2f2914318f2?w=600',
  complete: 'https://images.unsplash.com/photo-1676761136925-900da9b58200?w=600',
  auto: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=600',
  moto: 'https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=600',
  nautique: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=600',
  habitat: 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=600',
  dessin: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600'
};

export const OnlineCoursesPage = () => {
  const { isAdmin } = useAuth();
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);
  const adminMode = isAdmin && isAdmin();
  const [teaserEmail, setTeaserEmail] = useState('');
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  
  const category = searchParams.get('category') || '';

  useEffect(() => {
    if (adminMode) loadCourses();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, adminMode]);

  const handleTeaserSubmit = (e) => {
    e.preventDefault();
    if (!teaserEmail) return;
    toast.success('Merci ! Vous serez les premiers informés du lancement.');
    setTeaserEmail('');
  };

  // PUBLIC TEASING — for non-admins (modern video carousel teaser)
  if (!adminMode) {
    return <ElearningTeaserPublic teaserEmail={teaserEmail} setTeaserEmail={setTeaserEmail} handleTeaserSubmit={handleTeaserSubmit} />;
  }

  const loadCourses = async () => {
    try {
      setLoading(true);
      const filters = { course_type: 'online' };
      if (category) filters.category = category;
      const data = await api.getCourses(filters);
      setCourses(data);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course => 
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF7F0]">
      {/* Admin DEMO banner */}
      <div className="bg-[#1F2A4A] text-[#C9B896]">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3 text-sm">
          <Sparkles className="w-4 h-4" />
          <span><strong className="text-[#FAF7F0]">Mode démo admin</strong> · Cette section est en cours de développement et non visible par les visiteurs. Le grand public verra une page de teasing.</span>
        </div>
      </div>
      {/* Header */}
      <section className="px-6 py-8 md:py-12 bg-[#FAF7F0]">
        <div className="max-w-7xl mx-auto">
          <Badge className="bg-[#C9B896]/20 text-[#1F2A4A] border-[#C9B896]/40 mb-4">
            E-learning · Démo
          </Badge>
          <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-semibold mb-4 text-[#1F2A4A]" data-testid="online-courses-title">
            Académie en ligne (démo gamifiée)
          </h1>
          <p className="text-[#1F2A4A]/65 max-w-2xl text-lg">
            Aperçu interne du parcours e-learning. Premier épisode gratuit, modules suivants verrouillés derrière paiement.
            Progression XP, badges et déblocage par module.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="px-6 py-8 border-b border-[#1F2A4A]/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Rechercher une formation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border border-[#1F2A4A]/10 h-12"
              data-testid="search-courses"
            />
          </div>
          <Select 
            value={category || "all"} 
            onValueChange={(val) => setSearchParams(val && val !== "all" ? { category: val } : {})}
          >
            <SelectTrigger className="w-full md:w-64 bg-white border border-[#1F2A4A]/10 h-12" data-testid="filter-category">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Toutes les catégories" />
            </SelectTrigger>
            <SelectContent className="bg-white border border-[#1F2A4A]/10">
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {Object.entries(categoryLabels).map(([key, label]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      {/* Courses Grid */}
      <section className="px-6 py-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-lg overflow-hidden animate-pulse">
                  <div className="aspect-video bg-navy-light" />
                  <div className="p-6 space-y-3">
                    <div className="h-6 bg-navy-light rounded w-3/4" />
                    <div className="h-4 bg-navy-light rounded w-full" />
                    <div className="h-4 bg-navy-light rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Aucune formation trouvée</h3>
              <p className="text-muted-foreground">
                {searchTerm || category 
                  ? 'Essayez de modifier vos filtres de recherche.'
                  : 'Les formations en ligne seront bientôt disponibles.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCourses.map((course) => (
                <Link 
                  key={course.id}
                  to={L(`/formation/${course.id}`)}
                  className="group bg-white border border-[#1F2A4A]/10 rounded-lg overflow-hidden hover:border-beige/50 transition-all duration-500 card-hover"
                  data-testid={`course-card-${course.id}`}
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img 
                      src={course.thumbnail_url || categoryImages[course.category] || categoryImages.complete}
                      alt={course.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                      <Badge className="bg-[#C9B896]/90 text-[#1F2A4A]">
                        {categoryLabels[course.category] || course.category}
                      </Badge>
                      <div className="flex items-center gap-1 text-sm text-white">
                        <Play className="w-4 h-4" />
                        {course.episodes?.length || 0} épisodes
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-xl font-semibold mb-2 text-[#1F2A4A] group-hover:text-[#C9B896] transition-colors">
                      {course.title}
                    </h3>
                    <p className="text-[#1F2A4A]/65 text-sm line-clamp-2 mb-4">
                      {course.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-[#C9B896] font-semibold">
                        <Euro className="w-4 h-4" />
                        {course.price.toFixed(2)}
                      </div>
                      <Button size="sm" className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA]">
                        Voir le cours
                      </Button>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
